/**
 * Okyanus viewer asset loader.
 *
 * Fetches the revision manifest, reusable per-block-definition geometry
 * buffers and progressive instance chunks. Geometry is shared across all
 * panel instances (GPU instancing) so a repeated Rhino block is uploaded to
 * the GPU exactly once.
 */
import * as THREE from "three";
import { API } from "@/lib/api";

export function assetBase(projectId, revisionId) {
  return `${API}/projects/${projectId}/models/${revisionId}/assets`;
}

import { cacheKey, pruneOtherRevisions, readPackage, writePackage } from "./viewerCache";

async function fetchJson(url) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`Failed to load ${url} (${res.status})`);
  return res.json();
}

async function fetchBuffer(url) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`Failed to load ${url} (${res.status})`);
  return res.arrayBuffer();
}

function bufferBytes(definitions) {
  return definitions.reduce((sum, d) => sum + (d.buffer?.byteLength || 0), 0);
}

export function buildGeometries(buffer, definition) {
  return definition.parts.map((part) => {
    const geometry = new THREE.BufferGeometry();
    if (part.quantScale) {
      // Manifest v2: dequantise positions (uint16) and normals (int8).
      const quantised = new Uint16Array(buffer, part.posOffset, part.posBytes / 2);
      const positions = new Float32Array(quantised.length);
      const [mx, my, mz] = part.quantMin;
      const [sx, sy, sz] = part.quantScale;
      for (let i = 0; i < quantised.length; i += 3) {
        positions[i] = mx + quantised[i] * sx;
        positions[i + 1] = my + quantised[i + 1] * sy;
        positions[i + 2] = mz + quantised[i + 2] * sz;
      }
      geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

      const packed = new Int8Array(buffer, part.norOffset, part.norBytes);
      const normals = new Float32Array(packed.length);
      for (let i = 0; i < packed.length; i += 1) normals[i] = packed[i] / 127;
      geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));

      geometry.setIndex(
        new THREE.BufferAttribute(
          part.idxType === 2
            ? new Uint16Array(buffer, part.idxOffset, part.idxBytes / 2)
            : new Uint32Array(buffer, part.idxOffset, part.idxBytes / 4),
          1
        )
      );
      geometry.computeBoundingSphere();
      return { geometry, layer: part.layer, color: part.color };
    }
    geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(
        new Float32Array(buffer, part.posOffset, part.posBytes / 4),
        3
      )
    );
    geometry.setAttribute(
      "normal",
      new THREE.BufferAttribute(
        new Float32Array(buffer, part.norOffset, part.norBytes / 4),
        3
      )
    );
    geometry.setIndex(
      new THREE.BufferAttribute(
        new Uint32Array(buffer, part.idxOffset, part.idxBytes / 4),
        1
      )
    );
    geometry.computeBoundingSphere();
    return { geometry, layer: part.layer, color: part.color };
  });
}

/**
 * Structural parts of a panel: materials coded ANC (anchors), PR (profiles),
 * GL (glass) or SHT (sheets) — in the layer name (OKY_ANC_*, OKY_PROFILE_*,
 * OKY_GLASS_*, OKY_SHEET_*) or in the nested block name (ANC-*, *_Pn-PR-*,
 * *_Pn-GL-*, *_Pn-SHT-*). Gaskets, tapes and fasteners stick out of the panel
 * body and would distort the bounding box.
 * Falls back to every part when nothing matches, so a panel never loses its box.
 */
const STRUCTURAL_CODE = /(^|[\s_\-/.])(ANC|PR|PROFILE|GL|GLASS|SHT|SHEET)([\s_\-/.]|$)/i;

function structuralParts(definition) {
  const parts = definition.parts || [];
  const filtered = parts.filter(
    (part) => STRUCTURAL_CODE.test(part.layer || "") || STRUCTURAL_CODE.test(part.node || "")
  );
  return filtered.length ? filtered : parts;
}

/**
 * Local-space bounding box of a block definition, derived from the quantisation
 * bounds already stored in the manifest (v2) — no geometry download needed.
 * Returns null when the definition carries no quantised part (manifest v1).
 */
export function definitionBox(definition) {
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  let found = false;
  structuralParts(definition).forEach((part) => {
    if (!part.quantMin || !part.quantScale) return;
    found = true;
    for (let axis = 0; axis < 3; axis += 1) {
      const lo = part.quantMin[axis];
      const hi = lo + part.quantScale[axis] * 65535;
      if (lo < min[axis]) min[axis] = lo;
      if (hi > max[axis]) max[axis] = hi;
    }
  });
  return found ? { min, max } : null;
}

/**
 * One oriented box per structural part (its own local bbox + its own colour), so
 * L-shaped corner panels keep their silhouette instead of filling the whole
 * bounding rectangle, and PR / GL / SHT components stay visually distinct.
 */
function definitionPartBoxes(definition) {
  return structuralParts(definition)
    .filter((part) => part.quantMin && part.quantScale)
    .map((part) => {
      const base = { color: part.color || "#B0B7C3", layer: part.layer || "" };
      // Tilted parts carry a PCA oriented box (raked panels keep their true tilt).
      if (part.obbCenter && part.obbAxes && part.obbSize) {
        return {
          ...base,
          obb: { center: part.obbCenter, axes: part.obbAxes, size: part.obbSize },
        };
      }
      return {
        ...base,
        // Panes clipped to the PR frame carry explicit bounds; geometry encoding
        // fields (quantMin/quantScale) stay untouched for the detailed view.
        min: part.boxMin || [0, 1, 2].map((a) => part.quantMin[a]),
        max: part.boxMax || [0, 1, 2].map((a) => part.quantMin[a] + part.quantScale[a] * 65535),
      };
    });
}

export function packageHash(manifest) {
  return `${manifest.sourceHash || manifest.revisionHash || ""}-v${manifest.version || 1}`;
}

/**
 * Light view: manifest + instance chunks only (a few hundred KB) — the detailed
 * geometry buffers are never requested. Each definition gets an oriented box
 * derived from its quantisation bounds.
 */
export async function loadLightRevision({ projectId, revisionId, onManifest, onProgress }) {
  const base = assetBase(projectId, revisionId);
  const started = performance.now();
  const manifest = await fetchJson(`${base}/manifest.json`);
  onManifest?.(manifest);

  const definitions = [];
  let boxed = 0;
  manifest.definitions.forEach((definition) => {
    const box = definitionBox(definition);
    if (box) boxed += 1;
    definitions[definition.index] = { ...definition, box, partBoxes: definitionPartBoxes(definition) };
  });

  const instances = [];
  for (let i = 0; i < manifest.chunks.length; i += 1) {
    const chunk = await fetchJson(`${base}/${manifest.chunks[i].file}`);
    instances.push(...chunk);
    onProgress?.({ stage: "instances", loaded: instances.length, total: manifest.stats.panels });
  }

  return {
    manifest,
    definitions,
    instances,
    hash: packageHash(manifest),
    // False for legacy (non-quantised) packages: the caller falls back to the full load.
    boxesAvailable: boxed > 0 && boxed === manifest.definitions.length,
    telemetry: {
      light: true,
      cached: false,
      downloadMs: Math.round(performance.now() - started),
      bytes: 0,
      definitions: manifest.definitions.length,
      instances: instances.length,
      totalMs: performance.now() - started,
    },
  };
}

/** Detailed geometry of ONE block definition, cached per definition in IndexedDB. */
export async function loadDefinitionGeometry({ projectId, revisionId, definition, hash }) {
  const key = cacheKey(revisionId, `${hash}-def${definition.index}`);
  const cached = await readPackage(key);
  let buffer = cached?.buffer;
  if (!buffer) {
    buffer = await fetchBuffer(`${assetBase(projectId, revisionId)}/${definition.file}`);
    writePackage(key, { buffer });
  }
  return buildGeometries(buffer, definition);
}

/**
 * Loads the whole revision progressively.
 * onProgress({ stage, loaded, total }) is called throughout.
 */
export async function loadRevision({ projectId, revisionId, onManifest, onChunk, onProgress }) {
  const base = assetBase(projectId, revisionId);
  const started = performance.now();
  const manifest = await fetchJson(`${base}/manifest.json`);
  onManifest?.(manifest);

  // Version is part of the key: a re-processed revision (new geometry encoding)
  // must never be served from a stale cache.
  const key = cacheKey(
    revisionId,
    `${manifest.sourceHash || manifest.revisionHash || ""}-v${manifest.version || 1}`
  );
  const cached = await readPackage(key);
  if (cached) {
    const definitions = [];
    cached.definitions.forEach((definition) => {
      definitions[definition.index] = {
        ...definition,
        parts: buildGeometries(definition.buffer, definition),
      };
    });
    onChunk?.(cached.instances, 0);
    onProgress?.({
      stage: "instances",
      loaded: cached.instances.length,
      total: cached.instances.length,
      cached: true,
    });
    return {
      manifest,
      definitions,
      instances: cached.instances,
      telemetry: {
        cached: true,
        downloadMs: 0,
        bytes: bufferBytes(cached.definitions),
        definitions: cached.definitions.length,
        instances: cached.instances.length,
        totalMs: performance.now() - started,
      },
    };
  }

  const rawDefinitions = [];
  const definitions = [];
  for (let i = 0; i < manifest.definitions.length; i += 1) {
    const definition = manifest.definitions[i];
    const buffer = await fetchBuffer(`${base}/${definition.file}`);
    rawDefinitions.push({ ...definition, buffer });
    definitions[definition.index] = {
      ...definition,
      parts: buildGeometries(buffer, definition),
    };
    onProgress?.({
      stage: "geometry",
      loaded: i + 1,
      total: manifest.definitions.length,
    });
  }

  const instances = [];
  for (let i = 0; i < manifest.chunks.length; i += 1) {
    const chunk = await fetchJson(`${base}/${manifest.chunks[i].file}`);
    instances.push(...chunk);
    onChunk?.(chunk, i);
    onProgress?.({ stage: "instances", loaded: instances.length, total: manifest.stats.panels });
  }

  // Persist for instant re-opens; failures are silently ignored.
  writePackage(key, { definitions: rawDefinitions, instances });
  pruneOtherRevisions(revisionId);

  return {
    manifest,
    definitions,
    instances,
    telemetry: {
      cached: false,
      downloadMs: Math.round(performance.now() - started),
      bytes: bufferBytes(rawDefinitions),
      definitions: rawDefinitions.length,
      instances: instances.length,
      totalMs: performance.now() - started,
    },
  };
}

// Dev/CI hook: exercise the download + cache path without needing a WebGL context.
if (typeof window !== "undefined") {
  window.__oknLoadRevision = (projectId, revisionId) =>
    loadRevision({ projectId, revisionId }).then((r) => ({
      definitions: r.definitions.length,
      instances: r.instances.length,
      telemetry: r.telemetry,
    }));
}
